import React, { useMemo, useState } from 'react';
import { MdPanTool, MdCheck, MdUploadFile } from 'react-icons/md';
import { Button, Modal } from '@aibuild/ui';
import type { ProcessArtifactDto } from '@api/processes';
import type { ManualNodeData, Port, ProcessNode } from './graphTypes';
import type { ManualOutputFile } from './runSession/types';
import ArtifactDownloadList from './nodes/ArtifactDownloadList';
import {
  Body,
  EmptyHint,
  Explanation,
  FilePickerLabel,
  Instructions,
  NodeContext,
  NodeLabel,
  OutputPortLabel,
  OutputRow,
  Section,
  SectionTitle,
  SelectedFileName,
} from './ManualCompletionDialog.styled';

export interface ManualCompletionDialogProps {
  /** When non-null the dialog is open and points at this waiting manual node. */
  manualWait: { readonly nodeId: string; readonly iteration: number } | null;
  /** Live editor nodes — looked up to render the node's label + ports + instructions. */
  nodes: readonly ProcessNode[];
  /** Mirrored upstream input artifacts the assignee can download. */
  inputArtifacts: readonly ProcessArtifactDto[];
  /** True while the parent's complete mutation is in flight. */
  isCompleting: boolean;
  /** Uploads outputs + confirms completion. Throws on error so the dialog stays open. */
  onComplete: (outputs: readonly ManualOutputFile[]) => Promise<void>;
  /** Dismisses the dialog without completing. */
  onClose: () => void;
  /**
   * Presigned-URL refresh for the input download list. Required because
   * the dialog mounts OUTSIDE the editor context provider, so the list
   * can't read it from context.
   */
  refreshArtifactUrl?: (artifactId: string) => Promise<string>;
  dataTestId?: string;
}

const outputPortsOf = (data: ManualNodeData | undefined): readonly Port[] =>
  (data?.ports ?? []).filter((p) => p.direction === 'output');

/**
 * Modal for completing a manual node: the assignee downloads the mirrored
 * inputs, does the off-line work, attaches one output file per output port,
 * and confirms — which uploads the files and resumes the run.
 *
 * <p>Assignee authorisation is enforced server-side (403 for non-assignees);
 * this dialog surfaces to any run watcher.</p>
 */
const ManualCompletionDialog: React.FC<ManualCompletionDialogProps> = ({
  manualWait,
  nodes,
  inputArtifacts,
  isCompleting,
  onComplete,
  onClose,
  refreshArtifactUrl,
  dataTestId = 'manual-completion-dialog',
}) => {
  const node = manualWait ? nodes.find((n) => n.id === manualWait.nodeId) : undefined;
  const data = node?.data as ManualNodeData | undefined;
  const outputPorts = useMemo(() => outputPortsOf(data), [data]);

  const [filesByPort, setFilesByPort] = useState<Record<string, File>>({});

  const onPickFile = (portId: string, fileList: FileList | null): void => {
    const file = fileList?.[0];
    if (!file) return;
    setFilesByPort((prev) => ({ ...prev, [portId]: file }));
  };

  // Every output port must carry a file before the run can resume — the
  // manual node's output port is required on the backend.
  const allPortsFilled =
    outputPorts.length > 0 && outputPorts.every((p) => filesByPort[p.portId]);

  const handleComplete = async (): Promise<void> => {
    const outputs: ManualOutputFile[] = outputPorts
      .filter((p) => filesByPort[p.portId])
      .map((p) => {
        const file = filesByPort[p.portId];
        return { portId: p.portId, file, contentType: file.type || 'application/octet-stream' };
      });
    if (outputs.length === 0) return;
    try {
      await onComplete(outputs);
    } catch {
      // Parent surfaces the error via snackbar; keep the dialog open to retry.
    }
  };

  return (
    <Modal
      open={Boolean(manualWait)}
      onClose={isCompleting ? () => undefined : onClose}
      title="Complete manual step"
      subtitle="Download the inputs, do the work, then upload your results."
      icon={<MdPanTool />}
      size="md"
      dataTestId={dataTestId}
      hideCloseButton={isCompleting}
      disableEscape={isCompleting}
      disableBackdropClose={isCompleting}
      footer={
        <>
          <Button
            variant="text"
            onClick={onClose}
            disabled={isCompleting}
            dataTestId={`${dataTestId}__cancel`}
          >
            Not now
          </Button>
          <Button
            variant="filled"
            onClick={() => void handleComplete()}
            disabled={isCompleting || !allPortsFilled}
            leadingIcon={<MdCheck />}
            dataTestId={`${dataTestId}__complete`}
          >
            {isCompleting ? 'Completing…' : 'Complete step'}
          </Button>
        </>
      }
    >
      <Body data-testid={`${dataTestId}__body`}>
        <NodeContext>
          <NodeLabel data-testid={`${dataTestId}__node-label`}>
            {data?.label ?? 'Manual step'}
          </NodeLabel>
          {data?.instructions ? (
            <Instructions data-testid={`${dataTestId}__instructions`}>
              {data.instructions}
            </Instructions>
          ) : null}
        </NodeContext>

        <Section>
          <SectionTitle>Inputs to download</SectionTitle>
          {inputArtifacts.length > 0 ? (
            <ArtifactDownloadList
              artifacts={inputArtifacts}
              dataTestId={`${dataTestId}__inputs`}
              refreshArtifactUrl={refreshArtifactUrl}
            />
          ) : (
            <EmptyHint data-testid={`${dataTestId}__no-inputs`}>
              No input files for this step.
            </EmptyHint>
          )}
        </Section>

        <Section>
          <SectionTitle>Outputs to upload</SectionTitle>
          {outputPorts.length > 0 ? (
            outputPorts.map((port) => {
              const picked = filesByPort[port.portId];
              return (
                <OutputRow key={port.portId} data-testid={`${dataTestId}__output-${port.portId}`}>
                  <OutputPortLabel>{port.label}</OutputPortLabel>
                  <FilePickerLabel>
                    <MdUploadFile />
                    {picked ? (
                      <SelectedFileName title={picked.name}>{picked.name}</SelectedFileName>
                    ) : (
                      <span>Choose file</span>
                    )}
                    <input
                      type="file"
                      disabled={isCompleting}
                      data-testid={`${dataTestId}__file-${port.portId}`}
                      onChange={(e) => onPickFile(port.portId, e.target.files)}
                    />
                  </FilePickerLabel>
                </OutputRow>
              );
            })
          ) : (
            <EmptyHint data-testid={`${dataTestId}__no-outputs`}>
              This step has no output ports.
            </EmptyHint>
          )}
        </Section>

        <Explanation data-testid={`${dataTestId}__explanation`}>
          The run stays paused until an assigned user completes this step or the
          wait times out.
        </Explanation>
      </Body>
    </Modal>
  );
};

export default ManualCompletionDialog;
